import { StockOrderStatusEnum } from "@/requests/gen/react-query/fastAPI.schemas";
import { ColumnDef } from "@tanstack/react-table";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ParamDataTableToolbar,
  ParamSearchFilter,
  ParamClearButton,
  ParamDataTable,
  dataTableSearchParsers,
} from "@/components/ui/param-data-table";
import {
  useGetStockOrders,
  getGetStockOrdersQueryOptions,
} from "@/requests/gen/react-query/stock-order";
import formatUSD from "@/lib/format-currency";
import { format, parseISO } from "date-fns";
import {
  createFileRoute,
  Link,
  linkOptions,
  type LinkProps,
} from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { keepPreviousData } from "@tanstack/react-query";
import { Header } from "@/components/universal/header";
import {
  createLoader,
  parseAsArrayOf,
  parseAsStringEnum,
  useQueryStates,
  type inferParserType,
} from "nuqs";
import { DropdownFilter } from "@/components/ui/dropdown-filter";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const stockOrderSearchParsers = {
  statuses: parseAsArrayOf(
    parseAsStringEnum(Object.values(StockOrderStatusEnum)),
  ),
};

const stockOrderSearchAllParsers = {
  ...dataTableSearchParsers,
  ...stockOrderSearchParsers,
};

const loadStockOrderSearch = createLoader(stockOrderSearchAllParsers);

type StockOrderSearch = inferParserType<typeof stockOrderSearchAllParsers>;

function stockOrderQueryParams(
  p: StockOrderSearch,
): Parameters<typeof getGetStockOrdersQueryOptions>[0] {
  return {
    page: p.page,
    limit: p.limit,
    search: p.search,
    sort_column: p.sort_column,
    ascending: p.ascending,
    created_at_start: p.created_at_start,
    created_at_end: p.created_at_end,
    status: p.statuses,
  };
}

export const Route = createFileRoute("/_private/_stock-360/stock-order/lookup")(
  {
    loader: ({ context: { queryClient }, location }) =>
      queryClient.prefetchQuery(
        getGetStockOrdersQueryOptions(
          stockOrderQueryParams(loadStockOrderSearch(location.searchStr)),
        ),
      ),
    component: StockOrderLookupPage,
  },
);

// Define the columns for the table
interface StockOrderTableProps {
  link?: LinkProps;
  stock_order_id: string;
  products: string[];
  quantity: number;
  confirmed_at?: string;
  target_fulfill_by?: string;
  charged_at?: string;
  total: string;
  status: StockOrderStatusEnum | "";
}

// Define the columns headers and accessor keys
const columns: ColumnDef<StockOrderTableProps>[] = [
  { header: "Order ID", accessorKey: "stock_order_id" },
  {
    header: "Products",
    accessorKey: "products",
    cell: (cell) => {
      const products = cell.getValue() as string[];

      const firstProduct = products.at(0);
      const remainingCount = products.length - 1;

      return (
        <div className="flex items-center gap-1">
          <Badge variant={"secondary"}>{firstProduct}</Badge>
          {remainingCount > 0 && (
            <Tooltip>
              <TooltipTrigger className="bg-primary rounded-full text-white size-5 flex items-center justify-center text-xs">
                +{remainingCount}
              </TooltipTrigger>
              <TooltipContent>
                {products
                  .slice(1)
                  .map((product) => product)
                  .join(", ")}
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      );
    },
  },
  {
    header: "Confirmed At",
    accessorKey: "confirmed_at",
    cell: (value) => {
      const dateString = value.getValue() as string;
      if (!dateString) {
        return "--";
      }
      return format(parseISO(dateString), "MM-dd-yyyy");
    },
  },
  {
    header: "Target Fulfill By",
    accessorKey: "target_fulfill_by",
    cell: (value) => {
      const dateString = value.getValue() as string;
      if (!dateString) {
        return "--";
      }
      return format(parseISO(dateString), "MM-dd-yyyy");
    },
  },
  { header: "Total Amount", accessorKey: "total" },
  { header: "Total Quantity", accessorKey: "quantity" },
  {
    header: "Charged",
    accessorKey: "charged_at",
    cell: (value) => (value.getValue() ? "Yes" : "No"),
  },
  { header: "Status", accessorKey: "status" },
];

// Main component
function StockOrderLookupPage() {
  const [queryParams, setQueryParams] = useQueryStates(
    stockOrderSearchAllParsers,
  );

  // Get the stock_orders
  const stock_orders = useGetStockOrders(stockOrderQueryParams(queryParams), {
    query: {
      placeholderData: keepPreviousData,
    },
  });

  const tableData: StockOrderTableProps[] | undefined =
    stock_orders.data?.items.map((stock_order) => {
      // Extract all product names from the batches
      const products = stock_order.batches.map(
        (batch) =>
          batch.warehouse_vendor.warehouse.catalog_product.display_name,
      );

      const quantity = stock_order.batches.reduce((acc, batch) => {
        return acc + batch.order_quantity;
      }, 0);

      return {
        link: linkOptions({
          to: "/stock-order/$stockOrderId",
          params: { stockOrderId: String(stock_order.stock_order_id) },
        }),
        stock_order_id: stock_order.stock_order_id,
        confirmed_at: stock_order.confirmed_at ?? undefined,
        target_fulfill_by: stock_order.target_fulfill_by ?? undefined,
        charged_at: stock_order.charged_at ?? undefined,
        total: formatUSD(stock_order.total_price),
        status: stock_order.status,
        products,
        quantity,
      };
    });

  return (
    <div className="text-left">
      <title>Stock Orders - Legrande Health Portal</title>
      <Header
        crumbs={[{ name: "Stock Orders", link: { to: "/stock-order/lookup" } }]}
      />
      <Card>
        <CardHeader>
          <CardTitle>Stock Orders</CardTitle>
          <CardDescription>
            View recent bulk orders below. You can search, filter, and click to
            see more details.
          </CardDescription>
          <CardAction>
            <Button variant="default" size="sm" asChild>
              <Link to="/stock-order/create">
                <Plus />
                Create Stock Order
              </Link>
            </Button>
          </CardAction>
        </CardHeader>
        <CardContent>
          <ParamDataTableToolbar>
            <ParamSearchFilter isFetching={stock_orders.isFetching} />
            <DropdownFilter
              title="Status"
              options={[
                {
                  items: Object.values(StockOrderStatusEnum).map((status) => {
                    return { label: status, value: status };
                  }),
                },
              ]}
              values={queryParams.statuses || []}
              onChange={(values) => setQueryParams({ statuses: values })}
            />
            <ParamClearButton />
          </ParamDataTableToolbar>
          <ParamDataTable
            columns={columns}
            data={tableData ?? []}
            isLoading={stock_orders.isLoading}
            isError={stock_orders.isError}
            count={stock_orders.data?.count}
          />
        </CardContent>
      </Card>
    </div>
  );
}
